import React, { createContext, useCallback, useContext, useState } from 'react';
import i18n from 'i18next';
import { ECountry, EStorageKey } from 'common/const';
import { useStorage } from 'common/hooks/useStorage';
import { getOrganizationCountry, parseStoredCountry } from 'common/helpers/country.helper';
import { resolvePageLanguage } from 'common/helpers/language.helper';
import { IOrganization } from 'common/types/organization.types';

/* Действующая страна страницы (US-034, R-3): одна на все экраны и все страновые элементы
 *  (набор и выбор языка, суммы, комиссия, согласие, документы, cookie-попап, подвал).
 *  До ответа бэка и на экранах без организации (успех, ошибка PaySpot) — страна последней
 *  открытой на устройстве страницы со страной, иначе RS; ответ recipient_info её переключает. */
export interface ICountryContext {
  country: ECountry;
  /** Вызывается с организацией из ответа recipient_info: переключает и запоминает страну. */
  applyOrganizationCountry: (organization: IOrganization) => void;
}

const CountryContext = createContext<ICountryContext>({
  country: ECountry.RS,
  applyOrganizationCountry: () => undefined,
});

export const useCountryContext = () => useContext(CountryContext);

const getBrowserLanguage = (): string | undefined => navigator.languages?.[0] ?? navigator.language;

export const CountryProvider = ({ children }: { children: React.ReactNode }) => {
  const { getStorageItem, setStorageItem } = useStorage();
  const [country, setCountry] = useState<ECountry>(() => parseStoredCountry(getStorageItem(EStorageKey.Country)) ?? ECountry.RS);

  // Язык страницы решается по стране организации из ответа бэка (R-2), не по стране
  // с устройства: до ответа лоадер идёт на последнем языке устройства, и этот язык —
  // вход правила («последний язык» для RS и для ME с отметкой). Применять правило раньше,
  // по предварительной стране, нельзя: автоопределение ME затирало сохранённый язык до
  // того, как пришла настоящая страна (ревью кода US-034). Правило применяется на каждый
  // ответ, включая ответ с той же страной: у ME без отметки язык идёт от браузера.
  // changeLanguage пишет язык в localStorage 'lang' (F-4).
  const applyOrganizationCountry = useCallback((organization: IOrganization) => {
    const organizationCountry = getOrganizationCountry(organization);

    setStorageItem(EStorageKey.Country, organizationCountry);
    setCountry(organizationCountry);

    const language = resolvePageLanguage({
      country: organizationCountry,
      current: i18n.resolvedLanguage,
      chosen: getStorageItem(EStorageKey.LangChosen) === 'true',
      browserLanguage: getBrowserLanguage(),
    });

    if (language !== i18n.resolvedLanguage) {
      i18n.changeLanguage(language);
    }
  }, []);

  return <CountryContext.Provider value={{ country, applyOrganizationCountry }}>{children}</CountryContext.Provider>;
};
